<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  remarketingUploadsAPI,
  remarketingTemplatesAPI,
} from 'dashboard/api/remarketingSequences';

const store = useStore();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const isEdit = computed(() => !!route.params.id);
const isSaving = ref(false);

const form = reactive({
  name: '',
  description: '',
  active: true,
  trigger_type: 'label',
  inactivity_threshold_seconds: 1800,
  inbox_ids: [],
  exclude_label_list: [],
  trigger_label_list: [],
});

const makeEmptyStep = position => ({
  position,
  wait_duration_seconds: position === 0 ? 0 : 3600,
  content_type: 'text',
  content: '',
  template_params: null,
  attachment: null,
  attachment_signed_id: null,
  remove_attachment: false,
  uploading: false,
  upload_progress: 0,
  active: true,
});

const steps = ref([makeEmptyStep(0)]);

const inboxes = computed(() => store.getters['inboxes/getInboxes']);
const labels = computed(() => store.getters['labels/getLabels']);
const whatsappTemplates = ref([]);

const loadWhatsappTemplates = async () => {
  try {
    const { data } = await remarketingTemplatesAPI.get();
    whatsappTemplates.value = data || [];
  } catch (e) {
    whatsappTemplates.value = [];
  }
};

const onTemplateChange = (step, value) => {
  if (!value) return;
  const [name, language] = value.split('|');
  step.template_params = {
    ...(step.template_params || {}),
    name,
    language,
  };
};

const ACCEPTS = {
  image: 'image/png,image/jpeg,image/gif,image/webp',
  video: 'video/mp4,video/webm,video/quicktime',
  audio: 'audio/mpeg,audio/mp4,audio/ogg,audio/wav,audio/webm',
  file: '*',
};

const onContentTypeChange = step => {
  if (step.content_type !== 'template') step.template_params = null;
  if (!['image', 'video', 'audio', 'file'].includes(step.content_type)) {
    // keep existing attachment if user switches between media types? We reset to avoid confusion.
    step.attachment = null;
    step.attachment_signed_id = null;
  }
};

const uploadFile = async (step, file) => {
  if (!file) return;
  step.uploading = true;
  step.upload_progress = 0;
  try {
    const { data } = await remarketingUploadsAPI.upload(file, p => {
      step.upload_progress = p;
    });
    step.attachment_signed_id = data.signed_id;
    step.attachment = {
      filename: data.filename,
      content_type: data.content_type,
      byte_size: data.byte_size,
      url: data.url,
    };
    step.remove_attachment = false;
  } catch (e) {
    useAlert(e?.response?.data?.error || t('REMARKETING.ERROR.UPLOAD_FAILED'));
  } finally {
    step.uploading = false;
  }
};

const onFileChange = (step, event) => {
  const file = event.target.files?.[0];
  if (file) uploadFile(step, file);
};

const removeAttachment = step => {
  step.attachment = null;
  step.attachment_signed_id = null;
  step.remove_attachment = true;
};

const thresholdPreset = ref('30m');
const THRESHOLD_PRESETS = {
  '5m': 300,
  '30m': 1800,
  '1h': 3600,
  '3h': 10800,
  '1d': 86400,
  custom: null,
};

watch(thresholdPreset, v => {
  const secs = THRESHOLD_PRESETS[v];
  if (secs !== null) form.inactivity_threshold_seconds = secs;
});

const addStep = () => {
  steps.value.push(makeEmptyStep(steps.value.length));
};

const removeStep = idx => {
  steps.value.splice(idx, 1);
  steps.value.forEach((s, i) => {
    s.position = i;
  });
};

const goToList = () => {
  router.push({
    name: 'remarketing_list',
    params: { accountId: route.params.accountId },
  });
};

const handleSave = async () => {
  if (!form.name.trim()) {
    useAlert(t('REMARKETING.ERROR.NAME_REQUIRED'));
    return;
  }
  if (steps.value.length === 0) {
    useAlert(t('REMARKETING.ERROR.STEPS_REQUIRED'));
    return;
  }
  if (
    (form.trigger_type === 'label' || form.trigger_type === 'both') &&
    form.trigger_label_list.length === 0
  ) {
    useAlert(t('REMARKETING.ERROR.TRIGGER_LABEL_REQUIRED'));
    return;
  }
  const triggerLower = (form.trigger_label_list || []).map(l =>
    String(l).toLowerCase()
  );
  const excludeLower = (form.exclude_label_list || []).map(l =>
    String(l).toLowerCase()
  );
  const overlap = triggerLower.filter(l => excludeLower.includes(l));
  if (overlap.length > 0) {
    useAlert(t('REMARKETING.ERROR.LABEL_OVERLAP', { labels: overlap.join(', ') }));
    return;
  }
  for (let i = 0; i < steps.value.length; i += 1) {
    const s = steps.value[i];
    if (s.uploading) {
      useAlert(t('REMARKETING.ERROR.UPLOAD_IN_PROGRESS'));
      return;
    }
    const isMedia = ['image', 'video', 'audio', 'file'].includes(s.content_type);
    if (isMedia && !s.attachment && !s.attachment_signed_id) {
      useAlert(t('REMARKETING.ERROR.ATTACHMENT_REQUIRED'));
      return;
    }
    if (s.content_type === 'template' && !s.template_params?.name) {
      useAlert(t('REMARKETING.ERROR.TEMPLATE_NAME_REQUIRED'));
      return;
    }
  }

  isSaving.value = true;
  try {
    const payload = { sequence: { ...form }, steps: steps.value };
    if (isEdit.value) {
      await store.dispatch('remarketingSequences/update', {
        id: route.params.id,
        ...payload,
      });
      useAlert(t('REMARKETING.SAVED'));
    } else {
      await store.dispatch('remarketingSequences/create', payload);
      useAlert(t('REMARKETING.CREATED'));
    }
    goToList();
  } catch (e) {
    useAlert(
      e?.response?.data?.errors?.join(', ') ||
        t('REMARKETING.ERROR.SAVE_FAILED')
    );
  } finally {
    isSaving.value = false;
  }
};

onMounted(async () => {
  store.dispatch('inboxes/get');
  store.dispatch('labels/get');
  loadWhatsappTemplates();

  if (isEdit.value) {
    const seq = await store.dispatch(
      'remarketingSequences/show',
      route.params.id
    );
    Object.assign(form, {
      name: seq.name,
      description: seq.description || '',
      active: seq.active,
      trigger_type: seq.trigger_type,
      inactivity_threshold_seconds: seq.inactivity_threshold_seconds,
      inbox_ids: seq.inbox_ids || [],
      exclude_label_list: seq.exclude_label_list || [],
      trigger_label_list: seq.trigger_label_list || [],
    });
    if (seq.steps?.length) {
      steps.value = seq.steps.map(s => ({
        id: s.id,
        position: s.position,
        wait_duration_seconds: s.wait_duration_seconds,
        content_type: s.content_type,
        content: s.content || '',
        template_params: s.template_params || null,
        attachment: s.attachment || null,
        attachment_signed_id: null,
        remove_attachment: false,
        uploading: false,
        upload_progress: 0,
        active: s.active,
      }));
    }
    const match = Object.entries(THRESHOLD_PRESETS).find(
      ([, v]) => v === seq.inactivity_threshold_seconds
    );
    thresholdPreset.value = match ? match[0] : 'custom';
  }
});
</script>

<template>
  <SettingsLayout :is-loading="false">
    <template #header>
      <BaseSettingsHeader
        :title="isEdit ? t('REMARKETING.EDIT_TITLE') : t('REMARKETING.NEW_TITLE')"
        :description="t('REMARKETING.EDITOR_HINT')"
        :back-button-label="t('REMARKETING.HEADER')"
        feature-name="remarketing"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6 max-w-4xl">
        <!-- Basics card -->
        <div class="p-6 rounded-lg bg-n-alpha-black1 border border-n-weak">
          <div class="flex flex-col gap-4">
            <div>
              <label class="block mb-2 text-sm font-medium text-n-slate-12">
                {{ t('REMARKETING.FORM.NAME') }}
              </label>
              <input
                v-model="form.name"
                type="text"
                class="w-full px-3 py-2 text-sm border rounded-md bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
                :placeholder="t('REMARKETING.FORM.NAME_PLACEHOLDER')"
              />
            </div>

            <div>
              <label class="block mb-2 text-sm font-medium text-n-slate-12">
                {{ t('REMARKETING.FORM.DESCRIPTION') }}
              </label>
              <textarea
                v-model="form.description"
                rows="2"
                class="w-full px-3 py-2 text-sm border rounded-md bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
              />
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block mb-2 text-sm font-medium text-n-slate-12">
                  {{ t('REMARKETING.FORM.TRIGGER') }}
                </label>
                <select
                  v-model="form.trigger_type"
                  class="w-full px-3 py-2 text-sm border rounded-md bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:border-n-brand"
                >
                  <option value="label">
                    {{ t('REMARKETING.TRIGGER.LABEL') }}
                  </option>
                  <option value="inactivity">
                    {{ t('REMARKETING.TRIGGER.INACTIVITY') }}
                  </option>
                  <option value="both">
                    {{ t('REMARKETING.TRIGGER.BOTH') }}
                  </option>
                  <option value="manual">
                    {{ t('REMARKETING.TRIGGER.MANUAL') }}
                  </option>
                </select>
              </div>

              <div
                v-if="form.trigger_type === 'inactivity' || form.trigger_type === 'both'"
              >
                <label class="block mb-2 text-sm font-medium text-n-slate-12">
                  {{ t('REMARKETING.FORM.INACTIVITY_THRESHOLD') }}
                </label>
                <select
                  v-model="thresholdPreset"
                  class="w-full px-3 py-2 text-sm border rounded-md bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:border-n-brand"
                >
                  <option value="5m">5 {{ t('REMARKETING.UNITS.MIN') }}</option>
                  <option value="30m">30 {{ t('REMARKETING.UNITS.MIN') }}</option>
                  <option value="1h">1 {{ t('REMARKETING.UNITS.HOUR') }}</option>
                  <option value="3h">3 {{ t('REMARKETING.UNITS.HOURS') }}</option>
                  <option value="1d">1 {{ t('REMARKETING.UNITS.DAY') }}</option>
                  <option value="custom">{{ t('REMARKETING.UNITS.CUSTOM') }}</option>
                </select>
                <input
                  v-if="thresholdPreset === 'custom'"
                  v-model.number="form.inactivity_threshold_seconds"
                  type="number"
                  min="60"
                  class="mt-2 w-full px-3 py-2 text-sm border rounded-md bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:border-n-brand"
                  :placeholder="t('REMARKETING.FORM.SECONDS_PLACEHOLDER')"
                />
              </div>
            </div>

            <div
              v-if="form.trigger_type === 'label' || form.trigger_type === 'both'"
            >
              <label class="block mb-2 text-sm font-medium text-n-slate-12">
                {{ t('REMARKETING.FORM.TRIGGER_LABELS') }}
              </label>
              <select
                v-model="form.trigger_label_list"
                multiple
                class="w-full min-h-[100px] px-3 py-2 text-sm border rounded-md bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:border-n-brand"
              >
                <option
                  v-for="label in labels"
                  :key="label.id"
                  :value="label.title"
                >
                  {{ label.title }}
                </option>
              </select>
              <p class="mt-1 text-xs text-n-slate-10">
                {{ t('REMARKETING.FORM.TRIGGER_LABELS_HINT') }}
              </p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block mb-2 text-sm font-medium text-n-slate-12">
                  {{ t('REMARKETING.FORM.INBOXES') }}
                </label>
                <select
                  v-model="form.inbox_ids"
                  multiple
                  class="w-full min-h-[100px] px-3 py-2 text-sm border rounded-md bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:border-n-brand"
                >
                  <option
                    v-for="inbox in inboxes"
                    :key="inbox.id"
                    :value="inbox.id"
                  >
                    {{ inbox.name }}
                  </option>
                </select>
                <p class="mt-1 text-xs text-n-slate-10">
                  {{ t('REMARKETING.FORM.INBOXES_HINT') }}
                </p>
              </div>

              <div>
                <label class="block mb-2 text-sm font-medium text-n-slate-12">
                  {{ t('REMARKETING.FORM.EXCLUDE_LABELS') }}
                </label>
                <select
                  v-model="form.exclude_label_list"
                  multiple
                  class="w-full min-h-[100px] px-3 py-2 text-sm border rounded-md bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:border-n-brand"
                >
                  <option
                    v-for="label in labels"
                    :key="label.id"
                    :value="label.title"
                  >
                    {{ label.title }}
                  </option>
                </select>
                <p class="mt-1 text-xs text-n-slate-10">
                  {{ t('REMARKETING.FORM.EXCLUDE_LABELS_HINT') }}
                </p>
              </div>
            </div>

            <label class="flex items-center gap-2 text-sm text-n-slate-12">
              <input v-model="form.active" type="checkbox" />
              {{ t('REMARKETING.FORM.ACTIVE') }}
            </label>
          </div>
        </div>

        <!-- Steps card -->
        <div class="p-6 rounded-lg bg-n-alpha-black1 border border-n-weak">
          <h3 class="mb-2 text-lg font-semibold text-n-slate-12">
            {{ t('REMARKETING.STEPS_TITLE') }}
          </h3>
          <p class="mb-4 text-xs text-n-slate-10">
            {{ t('REMARKETING.VARIABLES_HELP') }}
          </p>

          <div class="flex flex-col gap-3">
            <div
              v-for="(step, idx) in steps"
              :key="idx"
              class="p-4 border rounded-md border-n-weak"
            >
              <div class="flex items-center justify-between mb-3">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('REMARKETING.STEP') }} {{ idx + 1 }}
                </span>
                <woot-button
                  v-if="steps.length > 1"
                  variant="clear"
                  color-scheme="alert"
                  size="small"
                  @click="removeStep(idx)"
                >
                  {{ t('REMARKETING.REMOVE_STEP') }}
                </woot-button>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div>
                  <label class="block mb-1 text-xs text-n-slate-11">
                    {{ t('REMARKETING.FORM.WAIT') }}
                  </label>
                  <input
                    v-model.number="step.wait_duration_seconds"
                    type="number"
                    min="0"
                    class="w-full px-3 py-2 text-sm border rounded-md bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:border-n-brand"
                    :placeholder="t('REMARKETING.FORM.SECONDS_PLACEHOLDER')"
                  />
                </div>
                <div>
                  <label class="block mb-1 text-xs text-n-slate-11">
                    {{ t('REMARKETING.FORM.CONTENT_TYPE') }}
                  </label>
                  <select
                    v-model="step.content_type"
                    class="w-full px-3 py-2 text-sm border rounded-md bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:border-n-brand"
                    @change="onContentTypeChange(step)"
                  >
                    <option value="text">
                      {{ t('REMARKETING.CONTENT_TYPE.TEXT') }}
                    </option>
                    <option value="image">
                      {{ t('REMARKETING.CONTENT_TYPE.IMAGE') }}
                    </option>
                    <option value="video">
                      {{ t('REMARKETING.CONTENT_TYPE.VIDEO') }}
                    </option>
                    <option value="audio">
                      {{ t('REMARKETING.CONTENT_TYPE.AUDIO') }}
                    </option>
                    <option value="file">
                      {{ t('REMARKETING.CONTENT_TYPE.FILE') }}
                    </option>
                    <option value="template">
                      {{ t('REMARKETING.CONTENT_TYPE.TEMPLATE') }}
                    </option>
                    <option value="ai_generated">
                      {{ t('REMARKETING.CONTENT_TYPE.AI') }}
                    </option>
                  </select>
                </div>
              </div>

              <!-- Media upload area -->
              <div
                v-if="['image', 'video', 'audio', 'file'].includes(step.content_type)"
                class="mb-3"
              >
                <label class="block mb-1 text-xs text-n-slate-11">
                  {{ t('REMARKETING.FORM.ATTACHMENT') }}
                </label>
                <div
                  v-if="step.attachment"
                  class="flex items-center justify-between p-3 mb-2 border rounded-md border-n-weak bg-n-alpha-black2"
                >
                  <div class="flex items-center gap-3 min-w-0">
                    <div class="i-lucide-paperclip text-n-slate-11" />
                    <div class="min-w-0">
                      <div class="text-sm text-n-slate-12 truncate">
                        {{ step.attachment.filename }}
                      </div>
                      <div class="text-xs text-n-slate-10">
                        {{ step.attachment.content_type }} ·
                        {{ Math.round(step.attachment.byte_size / 1024) }} KB
                      </div>
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    <a
                      v-if="step.attachment.url"
                      :href="step.attachment.url"
                      target="_blank"
                      rel="noopener"
                      class="text-xs text-n-brand hover:underline"
                    >
                      {{ t('REMARKETING.FORM.PREVIEW') }}
                    </a>
                    <woot-button
                      variant="clear"
                      color-scheme="alert"
                      size="small"
                      @click="removeAttachment(step)"
                    >
                      {{ t('REMARKETING.FORM.REMOVE_ATTACHMENT') }}
                    </woot-button>
                  </div>
                </div>
                <input
                  v-else
                  type="file"
                  :accept="ACCEPTS[step.content_type]"
                  :disabled="step.uploading"
                  class="block w-full text-sm text-n-slate-12 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:bg-n-brand file:text-white hover:file:bg-n-brand-hover file:cursor-pointer"
                  @change="onFileChange(step, $event)"
                />
                <div
                  v-if="step.uploading"
                  class="mt-2 text-xs text-n-slate-10"
                >
                  {{ t('REMARKETING.FORM.UPLOADING') }} {{ step.upload_progress }}%
                </div>
              </div>

              <!-- Template selector (dropdown of approved Meta templates) -->
              <div
                v-if="step.content_type === 'template'"
                class="mb-3 p-3 border rounded-md border-n-weak bg-n-alpha-black2"
              >
                <label class="block mb-1 text-xs text-n-slate-11">
                  {{ t('REMARKETING.FORM.TEMPLATE_PICKER') }}
                </label>
                <select
                  :value="
                    step.template_params?.name
                      ? `${step.template_params.name}|${step.template_params.language}`
                      : ''
                  "
                  class="w-full px-3 py-2 text-sm border rounded-md bg-n-alpha-black1 border-n-weak text-n-slate-12 focus:outline-none focus:border-n-brand"
                  @change="onTemplateChange(step, $event.target.value)"
                >
                  <option value="" disabled>
                    {{
                      whatsappTemplates.length === 0
                        ? t('REMARKETING.FORM.TEMPLATE_NONE')
                        : t('REMARKETING.FORM.TEMPLATE_PICK')
                    }}
                  </option>
                  <option
                    v-for="tpl in whatsappTemplates"
                    :key="`${tpl.name}|${tpl.language}|${tpl.inbox_id}`"
                    :value="`${tpl.name}|${tpl.language}`"
                  >
                    {{ tpl.name }} ({{ tpl.language }}) — {{ tpl.inbox_name }}
                  </option>
                </select>
                <p class="mt-1 text-xs text-n-slate-10">
                  {{ t('REMARKETING.FORM.TEMPLATE_PICKER_HINT') }}
                </p>
              </div>

              <div>
                <label class="block mb-1 text-xs text-n-slate-11">
                  {{
                    ['image', 'video', 'audio', 'file'].includes(step.content_type)
                      ? t('REMARKETING.FORM.CAPTION')
                      : t('REMARKETING.FORM.CONTENT')
                  }}
                </label>
                <textarea
                  v-model="step.content"
                  rows="3"
                  class="w-full px-3 py-2 text-sm font-mono border rounded-md bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
                  :placeholder="t('REMARKETING.FORM.CONTENT_PLACEHOLDER')"
                />
              </div>
            </div>
          </div>

          <div class="mt-4">
            <Button
              variant="outline"
              icon="i-lucide-circle-plus"
              :label="t('REMARKETING.ADD_STEP')"
              @click="addStep"
            />
          </div>
        </div>

        <!-- Action bar -->
        <div class="flex justify-end gap-3">
          <Button
            variant="outline"
            :label="t('REMARKETING.CANCEL')"
            @click="goToList"
          />
          <Button
            :label="isSaving ? t('REMARKETING.SAVING') : t('REMARKETING.SAVE')"
            :disabled="isSaving"
            @click="handleSave"
          />
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
